import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QuickWhatsAppDialog, EmailDialog } from "@/components/QuickComms";
import { CallLogDialog } from "@/pages/CallsPage";
import { AddTaskDialog, UploadDocumentDialog } from "@/components/DocumentsWorkspace";
import { CommStatusBadge, commLabel, EmptyState, WhatsAppIcon } from "@/components/common";
import { CustomerProfileEditButton } from "@/components/CustomerProfileEdit";
import { CaseTimelineFeed } from "@/components/CaseTimelineFeed";
import { Link } from "react-router-dom";
import {
  PhoneCall, Mail, CalendarClock, CheckSquare, StickyNote,
  Upload, ExternalLink, FileText, Send, CheckCircle2, ChevronDown, ChevronUp,
  Video,
} from "lucide-react";

// A custom_fields.{key} value may be a bare legacy scalar or the newer
// {value, source, set_by, set_at} provenance shape — every reader goes
// through this so callers never care which.
export const fieldDisplayValue = (v) => (v && typeof v === "object" && "source" in v && "value" in v) ? v.value : v;

/* Quick actions: Call | WhatsApp | Email | Book | Task | Note | Document */
export const QuickActions = ({ contact, opp, onBook, onRefresh, opportunities = [], cases = [], contactMethods }) => {
  const [waOpen, setWaOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [note, setNote] = useState("");

  const saveNote = async () => {
    try {
      await api.post(`/contacts/${contact.contact_id}/notes`, { note, opportunity_id: opp?.opportunity_id });
      toast.success("Note added"); setNoteOpen(false); setNote(""); onRefresh();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const A = ({ testId, icon: Icon, iconClassName, label, onClick }) => (
    <Button data-testid={testId} variant="outline" size="sm" className="h-8 text-xs" onClick={onClick}>
      <Icon className={iconClassName || "h-3.5 w-3.5 mr-1.5"} /> {label}
    </Button>
  );

  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-3" data-testid="c360-quick-actions">
      <A testId="qa-call" icon={PhoneCall} label="Call" onClick={() => setCallOpen(true)} />
      <A testId="qa-whatsapp" icon={WhatsAppIcon} iconClassName="h-3.5 w-3.5 mr-1.5 text-[#25D366]" label="WhatsApp" onClick={() => setWaOpen(true)} />
      <A testId="qa-email" icon={Mail} label="Email" onClick={() => setEmailOpen(true)} />
      <A testId="qa-book" icon={CalendarClock} label="Book consultation" onClick={onBook} />
      <A testId="qa-task" icon={CheckSquare} label="Add task" onClick={() => setTaskOpen(true)} />
      <A testId="qa-note" icon={StickyNote} label="Add note" onClick={() => setNoteOpen(true)} />
      <A testId="qa-doc" icon={Upload} label="Upload document" onClick={() => setDocOpen(true)} />
      {/* Canonical customer-profile editor — self-gated on
          crm.edit_own|edit_team|edit_any (admin via "*"). Lives in the
          primary action row so it's as easy to find as Call / Email. */}
      <CustomerProfileEditButton contact={contact} contactMethods={contactMethods} onSaved={onRefresh} />

      <QuickWhatsAppDialog contact={contact} open={waOpen} setOpen={setWaOpen} />
      <EmailDialog contact={contact} opportunityId={opp?.opportunity_id} open={emailOpen} setOpen={setEmailOpen} />
      {callOpen && (
        <CallLogDialog row={{ contact_id: contact.contact_id, contact_name: contact.full_name, opportunity_id: opp?.opportunity_id }}
          open={callOpen} setOpen={setCallOpen} onLogged={onRefresh} />
      )}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Add note</DialogTitle></DialogHeader>
          <Textarea data-testid="qa-note-text" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note…" />
          <DialogFooter><Button data-testid="qa-note-save" disabled={note.trim().length < 2} onClick={saveNote}>Save note</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AddTaskDialog open={taskOpen} setOpen={setTaskOpen} contactId={contact.contact_id}
        opportunities={opportunities} cases={cases} defaultOppId={opp?.opportunity_id} onCreated={onRefresh} />
      <UploadDocumentDialog open={docOpen} setOpen={setDocOpen} contactId={contact.contact_id}
        opportunities={opportunities} cases={cases} onUploaded={onRefresh} />
    </div>
  );
};

/* Documents tab */
export const DocumentsTab = ({ contactId }) => {
  const [docs, setDocs] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get(`/documents?contact_id=${contactId}`).then((r) => setDocs(r.data)), [contactId]);
  useEffect(() => { load(); }, [load]);
  const patch = async (id, body, msg) => {
    try { await api.patch(`/documents/${id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const CLS = { required: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]", uploaded: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]", verified: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", rejected: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]", expired: "bg-muted text-muted-foreground" };
  return (
    <Card><CardContent className="pt-4 space-y-2">
      {!docs ? null : docs.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" hint="Use the Document quick action to request or link the first document." testId="docs-empty" />
      ) : docs.map((d) => (
        <div key={d.document_id} className="p-2.5 rounded-md border space-y-1" data-testid={`doc-${d.document_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{d.name}</span>
            <Badge variant="outline" className="text-[9px]">{d.category}</Badge>
            <Badge variant="outline" className={`text-[10px] capitalize ${CLS[d.status] || ""}`}>{d.status}</Badge>
            {d.version > 0 && <span className="text-[10px] text-muted-foreground">v{d.version}</span>}
            {d.drive_link && (
              <a href={d.drive_link} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--info))] hover:underline inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Drive
              </a>
            )}
            <div className="ml-auto flex gap-1">
              {d.status === "uploaded" && (
                <>
                  <Button data-testid={`doc-verify-${d.document_id}`} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => patch(d.document_id, { status: "verified" }, "Document verified")}>Verify</Button>
                  <Button data-testid={`doc-reject-${d.document_id}`} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setReject(d)}>Reject</Button>
                </>
              )}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{(d.history || []).map((h) => h.event).join(" → ")}{d.uploaded_by ? ` · by ${d.uploaded_by}` : ""}</p>
        </div>
      ))}
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Reject document</DialogTitle></DialogHeader>
          <Input data-testid="doc-reject-reason" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button data-testid="doc-reject-confirm" variant="destructive" disabled={!reason.trim()}
              onClick={() => { patch(reject.document_id, { status: "rejected", reject_reason: reason }, "Document rejected"); setReject(null); setReason(""); }}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

/* Process cases tab — each Case with its unified history. `caseTimelines`
   is Customer 360's own payload (routers_crm.customer_360 calls the SAME
   case_timeline.case_unified_timeline the Case page's Timeline tab uses);
   further pages come from GET /cases/{id}/timeline via CaseTimelineFeed. */
export const CasesTab = ({ contactId, caseTimelines = {} }) => {
  const [cases, setCases] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => { api.get(`/cases?contact_id=${contactId}`).then((r) => setCases(r.data)); }, [contactId]);
  return (
    <Card><CardContent className="pt-4 space-y-2">
      {!cases ? null : cases.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No process cases — create one from Process / Cases after signup.</p>
      ) : cases.map((c) => (
        <div key={c.case_id} className="p-2.5 rounded-md border min-w-0" data-testid={`c360-case-${c.case_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Link className="font-mono text-xs underline decoration-dotted hover:text-primary" to={`/process/${c.case_id}`}>{c.case_id}</Link>
            <Badge variant="outline" className="text-[10px]">{c.product_code}</Badge>
            <span className="text-sm">{c.current_stage}</span>
            <Badge variant="outline" className="text-[10px] capitalize ml-auto">{c.status.replace(/_/g, " ")}</Badge>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 break-words">{(c.stages || []).join(" → ")}</p>
          <button type="button" className="mt-2 text-[11px] font-medium text-primary inline-flex items-center gap-1"
            data-testid={`c360-case-history-toggle-${c.case_id}`}
            onClick={() => setCollapsed((s) => ({ ...s, [c.case_id]: !s[c.case_id] }))}>
            {collapsed[c.case_id] ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            Case history{caseTimelines[c.case_id] ? ` (${caseTimelines[c.case_id].total})` : ""}
          </button>
          {!collapsed[c.case_id] && (
            <div className="mt-2">
              <CaseTimelineFeed caseId={c.case_id} initial={caseTimelines[c.case_id]} pageSize={20}
                testId={`c360-case-timeline-${c.case_id}`} />
            </div>
          )}
        </div>
      ))}
    </CardContent></Card>
  );
};

/* Tickets tab */
export const TicketsTab = ({ contactId }) => {
  const [tickets, setTickets] = useState(null);
  useEffect(() => { api.get(`/tickets?contact_id=${contactId}`).then((r) => setTickets(r.data)); }, [contactId]);
  return (
    <Card><CardContent className="pt-4 space-y-2">
      {!tickets ? null : tickets.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No tickets or complaints for this customer.</p>
      ) : tickets.map((t) => (
        <div key={t.ticket_id} className="p-2.5 rounded-md border flex items-center gap-2 flex-wrap" data-testid={`c360-ticket-${t.ticket_id}`}>
          <span className="font-mono text-xs">{t.ticket_id}</span>
          <span className="text-sm flex-1 truncate">{t.subject}</span>
          <Badge variant="outline" className="text-[10px] capitalize">{t.priority}</Badge>
          <Badge variant="outline" className="text-[10px] capitalize">{t.status.replace(/_/g, " ")}</Badge>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground">Manage tickets in the Support module.</p>
    </CardContent></Card>
  );
};

/* Calls tab: call interactions only */
export const CallsTab = ({ interactions }) => {
  const calls = (interactions || []).filter((i) => i.type === "call");
  return (
    <Card><CardContent className="pt-4 space-y-2">
      {calls.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No calls logged yet — use the Call quick action.</p>
      ) : calls.map((c) => (
        <div key={c.interaction_id} className="p-2 rounded-md border text-sm" data-testid={`c360-call-${c.interaction_id}`}>
          <div className="flex items-center gap-2">
            <PhoneCall className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1">{c.summary}</span>
            {c.outcome && <Badge variant="outline" className="text-[10px]">{c.outcome.replace(/_/g, " ")}</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{fmtIST(c.created_at)}</p>
        </div>
      ))}
    </CardContent></Card>
  );
};

/* Communications tab: whatsapp/email interactions + outbox statuses */
export const CommsTab = ({ interactions, communications }) => {
  const comms = (interactions || []).filter((i) => ["whatsapp", "email"].includes(i.type));
  return (
    <div className="space-y-3">
      {communications?.length > 0 && (
        <Card><CardContent className="pt-4 space-y-1.5">
          <p className="text-sm font-semibold flex items-center gap-2"><Send className="h-4 w-4" /> Automated sends</p>
          {communications.slice(0, 10).map((c) => (
            <div key={c.outbox_id} className="flex items-center gap-2 text-sm flex-wrap">
              <span className="w-24 shrink-0 text-xs text-muted-foreground">{fmtIST(c.created_at)}</span>
              <span className="flex-1">{commLabel(c)}</span>
              <CommStatusBadge status={c.status} />
            </div>
          ))}
        </CardContent></Card>
      )}
      <Card><CardContent className="pt-4 space-y-2">
        <p className="text-sm font-semibold">Manual communications</p>
        {comms.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No WhatsApp/email interactions yet — use the quick actions.</p>
        ) : comms.map((c) => (
          <div key={c.interaction_id} className="p-2 rounded-md border text-sm">
            <div className="flex items-center gap-2">
              {c.type === "whatsapp" ? <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> : <Mail className="h-3.5 w-3.5 text-muted-foreground" />}
              <span className="flex-1">{c.summary}</span>
              {c.outcome && <Badge variant="outline" className="text-[10px]">{c.outcome.replace(/_/g, " ")}</Badge>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{fmtIST(c.created_at)}</p>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
};

/* Profile tab: standard + product-specific custom fields */
// Generic, product-independent per-submission answer card — Form Name,
// Source/Channel, Campaign, Submission time, Question→Answer — reused by
// both Customer 360's Profile tab (latest submission) and History tab
// (every submission). Works for any channel that preserves a raw-answer
// snapshot (meta_form_answers, sheet_row_answers) via the same shape
// {field_key, label, value}; a channel with none just shows nothing here.
// Meta/Sheets question labels are the raw machine field key (snake_case or
// camelCase, e.g. "budget_range_for_this_specific_trip") — this is purely a
// DISPLAY transform (Title Case, underscores/camelCase → spaces); the raw
// key itself is never altered, so mapping matching against it is unaffected.
const humanizeFieldKey = (key) => {
  const s = String(key ?? "");
  if (!s) return s;
  return s
    .replaceAll("_", " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const AnswerRow = ({ label, value }) => {
  const [expanded, setExpanded] = useState(false);
  const text = Array.isArray(value) ? value.join(", ") : (value ?? "");
  const long = String(text).length > 160;
  return (
    <p className="text-xs text-muted-foreground min-w-0">
      <span className="text-foreground [overflow-wrap:anywhere]" title={label}>{humanizeFieldKey(label)}</span> →{" "}
      <span className="break-words [overflow-wrap:anywhere]">{long && !expanded ? `${String(text).slice(0, 160)}…` : text}</span>
      {long && (
        <button type="button" className="ml-1 text-primary underline shrink-0" onClick={() => setExpanded((e) => !e)}>
          {expanded ? "less" : "more"}
        </button>
      )}
    </p>
  );
};

// Which ingestion path created this lead — the exact provenance an admin
// needs to tell a genuine native-Meta lead apart from one that arrived
// through the generic Pabbly/n8n bridge tagged with a Meta-like source.
const CHANNEL_LABEL = {
  meta: "Meta (native integration)",
  webhook: "Generic webhook (Pabbly/n8n)",
  google_sheets: "Google Sheets",
  manual: "Manual entry",
};

const LeadFormInfoCard = ({ intake }) => {
  const answers = intake.meta_form_answers || intake.sheet_row_answers || [];
  const formLabel = intake.meta_form_name || (intake.channel === "google_sheets" ? "Google Sheets row" : intake.channel);
  return (
    <div className="rounded-md border bg-muted/40 p-2.5 min-w-0 overflow-hidden" data-testid={`lead-form-info-${intake.lead_id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1">
        <p className="text-xs font-medium">{formLabel || "Lead form"}</p>
        <Badge variant="outline" className="text-[9px]" data-testid={`lead-form-channel-${intake.lead_id}`}>
          {CHANNEL_LABEL[intake.channel] || intake.channel || "unknown source"}
        </Badge>
        <span className="text-[11px] text-muted-foreground">{intake.source}{intake.campaign ? ` / ${intake.campaign}` : ""} · {fmtIST(intake.created_at)}</span>
      </div>
      {answers.filter((a) => a.value !== null && a.value !== "").length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
          {answers.filter((a) => a.value !== null && a.value !== "").map((a, i) => (
            <AnswerRow key={i} label={a.label} value={a.value} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No per-question answers preserved for this channel.</p>
      )}
      {(intake.mapping_issues || []).length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-dashed">
          <p className="text-[11px] text-[hsl(var(--warning))]">
            {intake.mapping_issues.length} answer{intake.mapping_issues.length === 1 ? "" : "s"} didn't map to a structured field: {" "}
            {intake.mapping_issues.map((i) => `${i.field_key} (${i.reason})`).join("; ")}
          </p>
        </div>
      )}
    </div>
  );
};

export const LeadFormInfo = ({ leadIntakes = [], productCode, latestOnly = false }) => {
  const [showAll, setShowAll] = useState(false);
  const filtered = productCode ? leadIntakes.filter((l) => l.product_code === productCode) : leadIntakes;
  const withAnswers = filtered.filter((l) => (l.meta_form_answers?.length || l.sheet_row_answers?.length));
  const sorted = [...withAnswers].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  if (!sorted.length) return null;
  const toShow = latestOnly && !showAll ? sorted.slice(0, 1) : sorted;
  return (
    <div className="space-y-2">
      {toShow.map((l) => <LeadFormInfoCard key={l.lead_id} intake={l} />)}
      {latestOnly && sorted.length > 1 && (
        <button type="button" className="text-xs text-primary underline" onClick={() => setShowAll((s) => !s)}>
          {showAll ? "Show latest only" : `Show ${sorted.length - 1} earlier submission(s)`}
        </button>
      )}
    </div>
  );
};

const PROVENANCE_LABEL = {
  employee_edit: { label: "Edited", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  employee_verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  lead_intake: { label: "From lead form", cls: "bg-muted text-muted-foreground" },
};

const FieldProvenance = ({ raw, entity, recordId, fieldKey, onVerified }) => {
  const source = raw && typeof raw === "object" ? raw.source : (raw !== undefined && raw !== null && raw !== "" ? "legacy" : null);
  if (!source || source === "legacy") return null;
  const meta = PROVENANCE_LABEL[source];
  const verify = async () => {
    try {
      await api.post(`/custom-fields/values/${entity}/${recordId}/verify`, { key: fieldKey });
      toast.success(`${meta?.label === "Verified" ? "Already" : "Marked"} verified`);
    } catch (e) { toast.error(errDetail(e)); }
    onVerified?.();
  };
  return (
    <div className="flex items-center gap-1.5">
      {meta && <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border-0 ${meta.cls}`}>{meta.label}</Badge>}
      {source === "lead_intake" && (
        <button type="button" data-testid={`cf-verify-${fieldKey}`} title="Confirm this value is correct"
          className="text-muted-foreground hover:text-emerald-600" onClick={verify}>
          <CheckCircle2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export const ProfileTab = ({ contact, opp, onRefresh, leadIntakes = [] }) => {
  const [contactDefs, setContactDefs] = useState([]);
  const [oppDefs, setOppDefs] = useState([]);
  const [contactValues, setContactValues] = useState({});
  const [oppValues, setOppValues] = useState({});
  const [oppRecord, setOppRecord] = useState(null);

  const load = useCallback(() => {
    const qs = opp ? `&product_code=${opp.product_code}` : "";
    api.get(`/custom-fields?entity=contact${qs}`).then((r) => setContactDefs(r.data));
    const raw = {};
    Object.entries(contact.custom_fields || {}).forEach(([k, v]) => { raw[k] = fieldDisplayValue(v); });
    setContactValues(raw);
    if (opp?.opportunity_id) {
      api.get(`/custom-fields?entity=opportunity${qs}`).then((r) => setOppDefs(r.data));
      api.get(`/opportunities?opportunity_id=${opp.opportunity_id}`).then((r) => {
        const o = r.data?.[0];
        setOppRecord(o || null);
        const rawO = {};
        Object.entries(o?.custom_fields || {}).forEach(([k, v]) => { rawO[k] = fieldDisplayValue(v); });
        setOppValues(rawO);
      });
    } else {
      setOppDefs([]); setOppValues({}); setOppRecord(null);
    }
  }, [contact, opp]);
  useEffect(load, [load]);

  const save = async () => {
    try {
      if (contactDefs.length) {
        await api.patch(`/custom-fields/values/contact/${contact.contact_id}`, { values: contactValues });
      }
      if (oppDefs.length && opp?.opportunity_id) {
        await api.patch(`/custom-fields/values/opportunity/${opp.opportunity_id}`, { values: oppValues });
      }
      toast.success("Profile saved"); load(); onRefresh?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const renderField = (f, values, setValues, rawSource, recordId) => {
    const raw = rawSource?.[f.key];
    const provRaw = raw && typeof raw === "object" ? raw : undefined;
    return (
      <div key={f.key} className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">{f.label}{f.required && " *"}</Label>
          <FieldProvenance raw={provRaw} entity={f.entity} recordId={recordId}
            fieldKey={f.key} onVerified={load} />
        </div>
        {f.type === "dropdown" ? (
          <Select value={values[f.key] || ""} onValueChange={(v) => setValues({ ...values, [f.key]: v })}>
            <SelectTrigger data-testid={`cf-${f.key}`} className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ) : f.type === "multiselect" ? (
          <Input data-testid={`cf-${f.key}`} className="h-8 text-sm" placeholder={(f.options || []).join(", ")}
            value={Array.isArray(values[f.key]) ? values[f.key].join(", ") : (values[f.key] ?? "")}
            onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
        ) : f.type === "boolean" ? (
          <Select value={String(values[f.key] ?? "")} onValueChange={(v) => setValues({ ...values, [f.key]: v === "true" })}>
            <SelectTrigger data-testid={`cf-${f.key}`} className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
          </Select>
        ) : f.type === "longtext" ? (
          <Textarea data-testid={`cf-${f.key}`} className="text-sm" rows={2}
            value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
        ) : (
          <Input data-testid={`cf-${f.key}`} className="h-8 text-sm"
            type={f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : "text"}
            value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
        )}
      </div>
    );
  };

  const bySection = (defs) => defs.reduce((m, d) => { (m[d.section] = m[d.section] || []).push(d); return m; }, {});
  const contactSections = bySection(contactDefs);
  const oppSections = bySection(oppDefs);
  const nothing = contactDefs.length === 0 && oppDefs.length === 0;

  return (
    <Card><CardContent className="pt-4 space-y-4">
      <LeadFormInfo leadIntakes={leadIntakes} productCode={opp?.product_code} latestOnly />
      {nothing && <p className="text-sm text-muted-foreground text-center py-6">No custom fields configured for this product yet (Admin → Custom Fields).</p>}
      {Object.entries(contactSections).map(([section, fields]) => (
        <div key={`c-${section}`} className="space-y-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground">{section}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => renderField(f, contactValues, setContactValues, contact.custom_fields, contact.contact_id))}
          </div>
        </div>
      ))}
      {Object.entries(oppSections).map(([section, fields]) => (
        <div key={`o-${section}`} className="space-y-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground">{section} <span className="normal-case text-muted-foreground/70">(this product)</span></p>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => renderField(f, oppValues, setOppValues, oppRecord?.custom_fields, opp?.opportunity_id))}
          </div>
        </div>
      ))}
      {!nothing && (
        <div className="flex justify-end"><Button data-testid="cf-save" size="sm" onClick={save}>Save profile</Button></div>
      )}
    </CardContent></Card>
  );
};

/* Webinar tab (Workstream B): this customer's webinar funnel history —
 * campaign/session, registration + provider join state, the reminders that
 * were actually queued, attendance + segment, CTA history and the Anika
 * follow-up. Payload-fed from customer_360's own `webinars` key, like the
 * other non-fetching tabs here; the backend already applies an explicit
 * whitelist projection, so no raw join URL or message body reaches the UI. */
const SEGMENT_STYLES = {
  no_show: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  attended_under_50: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  attended_50_74: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  attended_75_plus: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
};

export const WebinarsTab = ({ webinars }) => {
  const rows = webinars || [];
  return (
    <Card><CardContent className="pt-4 space-y-2">
      {rows.length === 0 ? (
        <EmptyState icon={Video} title="No webinar activity"
          hint="This customer has not been enrolled in a webinar campaign."
          testId="c360-webinar-empty" />
      ) : rows.map((w) => (
        <div key={w.enrollment_id} className="p-2.5 rounded-md border space-y-1.5"
             data-testid={`c360-webinar-${w.enrollment_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Video className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">{w.campaign_name || "Webinar"}</span>
            <Badge variant="outline" className="text-[10px] capitalize">{w.campaign_type}</Badge>
            {w.segment && (
              <Badge variant="outline" className={`text-[10px] ${SEGMENT_STYLES[w.segment] || ""}`}>
                {w.segment_label}
              </Badge>
            )}
            {w.is_test_record && <Badge variant="outline" className="text-[10px]">TEST</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Session {w.session_start ? `${fmtIST(w.session_start, { year: true })} IST` : "—"}
            {" · "}enrolled {fmtIST(w.enrolled_at)}
            {w.source ? ` · via ${w.source}` : ""}
          </p>
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <Badge variant="outline" className="text-[10px]">
              {(w.registration_state || "").replace(/_/g, " ")}
            </Badge>
            {w.has_join_link && <span className="text-muted-foreground">unique join link issued</span>}
            {w.provider_attendee_ref && (
              <span className="text-muted-foreground font-mono">
                ref {String(w.provider_attendee_ref).slice(0, 14)}
              </span>
            )}
            {w.registration_error && (
              <span className="text-[hsl(var(--danger))]">{w.registration_error}</span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Attendance:{" "}
            {w.attendance?.state === "attended"
              ? `${w.attendance.duration_minutes} min (${w.attendance.attended_pct}%)`
              : (w.attendance?.state || "unknown").replace(/_/g, " ")}
            {" · "}reminders queued: {(w.messages || []).length}
          </p>
          {(w.messages || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {w.messages.map((m, i) => (
                <Badge key={`${m.kind}-${i}`} variant="outline" className="text-[10px]">
                  {String(m.kind || "").replace(/_/g, " ")} · {fmtIST(m.at)}
                </Badge>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            CTA: {w.cta?.eligible === true ? "eligible" : w.cta?.eligible === false ? "not eligible" : "—"}
            {w.cta?.message_sent_at ? ` · message sent ${fmtIST(w.cta.message_sent_at)}` : ""}
            {w.cta?.task_id ? " · follow-up task created" : ""}
            {w.anika_followup?.state ? ` · Anika ${w.anika_followup.state}` : ""}
          </p>
        </div>
      ))}
    </CardContent></Card>
  );
};
